import { RatesTableState, RatesHighlight } from "app/pages/AgentPricing/modules/RatesTableWidget/state";
import { Action, ActionType, ChangeSchoolOverrideSpec } from "app/pages/AgentPricing/actionTypes";
import { AgentCommissionDto, AgentCommissionRateQueryResult, AgentCommissionRateQueryStatus, AgentCommissionRateQueryDto, AgentCommissionStatus } from "app/shared/model/AgentCommissionDto";
import { Country, School } from "app/shared/model";
import { combineReducers } from "redux";
import { parseISO, isBefore as isDateBefore, format as formatDate } from 'date-fns';
import { mapToRatesTable, RatesTable, Rates, RatesRule, CountryGroup } from "app/pages/AgentPricing/models";

export function table(state: RatesTable|undefined, action: Action) : RatesTable {
    if (!state) {
        return {
            rules: [],
            versionCode: '',
            countries: [],
            rates: {},
            dates: {
                startDate: '1900-01-01',
                minChangeDate: '2999-01-01'
            },
            status: AgentCommissionStatus.New
        };
    }

    switch(action.type) {
        case ActionType.ViewPageLoaded:
            return mapToRatesTable(action.agentCommission, action.data.countries, action.data.schools, false);
        case ActionType.EditPageLoaded:
            return mapToRatesTable(action.agentCommission, action.data.countries, action.data.schools, true);
        case ActionType.ChangeRate:
            return {
                ... state,
                rates: {
                    ... state.rates,
                    [action.columnId + '|' + action.ruleId]: action.rate
                }
            };

        case ActionType.ChangeStartDate:
            if (isDateBefore(parseISO(action.newDate), parseISO(state.dates.minChangeDate)))
                return state;

            return {
                ...state,
                dates: {
                    ...state.dates,
                    startDate: action.newDate
                }
            };

        case ActionType.ChangeEndDate:
            return {
                ...state,
                dates: {
                    ...state.dates,
                    endDate: action.newDate ?? undefined
                }
            };

        case ActionType.ChangeSchoolOverrides:
            return {
                ... state,
                countries: updateColumnsForNewOverrides(
                    state.countries,
                    action.schoolOverrides
                ),
                rates: updateRatesTableForNewOverrides(
                    state.rates,
                    state.rules,
                    action.schoolOverrides
                )
            }

        default: 
            return state; 
    }
}

export function highlight(state: RatesHighlight|undefined, action: Action) : RatesHighlight {
    if (!state) {
        return {
            ruleCode: undefined,
            columnId: undefined
        }
    }

    switch(action.type) {
        case ActionType.ChangeRateFinderCriteria: 
            if (action.itemId == undefined && action.schoolId == undefined) {
                return {
                    ruleCode: undefined,
                    columnId: undefined
                } 
            }
            else 
                return state;             
        case ActionType.RateFinderQueryRunning: 
            return {
                ruleCode: undefined,
                columnId: undefined
            }
        case ActionType.RateFinderQueryResult: 
            return mapToRatesHighlight(action.request, action.result); 
        default: 
            return state; 
    }
}

function mapToRatesHighlight(request: AgentCommissionRateQueryDto, result: AgentCommissionRateQueryResult) : RatesHighlight {
    if (result.status == AgentCommissionRateQueryStatus.Errored) {
        return {
            columnId: undefined, 
            ruleCode: undefined
        }
    }

    const isProductSelected = request.itemId !== undefined && request.itemType !== undefined; 
    if (isProductSelected && result.ruleId === undefined) {
        return {
            columnId: undefined, 
            ruleCode: undefined
        }
    }

    return {
        columnId: result.locationId, 
        ruleCode: result.ruleId
    }    
}



function updateRatesTableForNewOverrides(
    currentRates: Rates,
    rules: RatesRule[],
    changeSpecs: ChangeSchoolOverrideSpec[]) : Rates {

    const newRates : Rates = {};
    const ruleCodes = rules.map(r => r.code);

    // Copy countries
    Object.keys(currentRates).filter(k => !k.startsWith('School-')).forEach(k => {
        newRates[k] = currentRates[k];
    });

    // add schools
    changeSpecs.forEach(spec => {
        ruleCodes.forEach(ruleCode => {
            const schoolKey = spec.schoolCode + '|' + ruleCode;
            const countryKey = spec.countryCode + '|' + ruleCode;
            if (currentRates.hasOwnProperty(schoolKey))
                newRates[schoolKey] = currentRates[schoolKey];
            else if (currentRates.hasOwnProperty(countryKey))
                newRates[schoolKey] = currentRates[countryKey];
            else
                newRates[schoolKey] = '';
        });
    });

    return newRates;
}

function updateColumnsForNewOverrides(columns: CountryGroup[], changeSpecs: ChangeSchoolOverrideSpec[]) {
    return columns.map(c => {

        const newColumns = changeSpecs.filter(spec => spec.countryCode === c.id).map(x => (
            {id: x.schoolCode, name: x.schoolName, isSchool: true}
        ));

        const countryColumnCaption = newColumns.length == 0
            ? 'All schools'
            : 'Other schools';

        const countryColumn = {id: c.id, name: countryColumnCaption, isSchool: false}
        newColumns.push(countryColumn);

        return {
            ... c,
            columns: newColumns
        }
    })
}

export const reducer = combineReducers({
    table,
    highlight
});